import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import DashboardLayout from '../components/DashboardLayout'
import { useAuth, authenticatedFetch } from '../hooks/useAuth'
import { useI18n } from '../i18n/index.jsx'

export default function Memory3D() {
  const { user } = useAuth()
  const { t } = useI18n()
  const mountRef = useRef(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [pointsCount, setPointsCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  // Fetch stats to set points equal to total_vectors
  useEffect(() => {
    let cancelled = false
    async function fetchStats() {
      try {
        setLoading(true)
        setErr('')
  const res = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/stats', { method: 'GET' })
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)
        const data = await res.json()
        const total = Number(data?.total_vectors || 0)
        if (!cancelled) setPointsCount(Number.isFinite(total) ? total : 0)
      } catch (e) {
  if (!cancelled) setErr(e.message || t('stats.loading'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (user) fetchStats()
    return () => { cancelled = true }
  }, [user])

  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return

  const width = mount.clientWidth || 800
  let height = Math.round((width * 9) / 16)
  if (!height || height < 300) height = 360

    // Scene
    const scene = new THREE.Scene()
    scene.fog = new THREE.FogExp2(0x030712, 0.002)

    // Camera
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000)
    camera.position.set(0, 0, 120)

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(0x000000, 1)
    mount.appendChild(renderer.domElement)

  // Simple light
    const light = new THREE.AmbientLight(0xffffff, 0.8)
  scene.add(light)
  // Point cloud sized from total_vectors (clamped)
  const MAX_POINTS = 20000
  const DEFAULT_POINTS = 2000
  const requested = pointsCount > 0 ? pointsCount : DEFAULT_POINTS
  const POINTS = Math.max(1, Math.min(requested, MAX_POINTS))
    
    const positions = new Float32Array(POINTS * 3)
    const colors = new Float32Array(POINTS * 3)
    
    // Generate random points in a spherical cloud
    for (let i = 0; i < POINTS; i++) {
      // Random position in sphere
      const radius = 30 + Math.random() * 20
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      
      const x = radius * Math.sin(phi) * Math.cos(theta)
      const y = radius * Math.cos(phi)
      const z = radius * Math.sin(phi) * Math.sin(theta)
      
      positions[i * 3] = x
      positions[i * 3 + 1] = y
      positions[i * 3 + 2] = z
      
      // Blue color like your images
      colors[i * 3] = 0.2 + Math.random() * 0.3     // red
      colors[i * 3 + 1] = 0.3 + Math.random() * 0.4 // green
      colors[i * 3 + 2] = 0.8 + Math.random() * 0.2 // blue
    }
    
  const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    
    const material = new THREE.PointsMaterial({
      size: 2.0,
      vertexColors: true,
      transparent: false,
      opacity: 1.0
    })
    
    const pointCloud = new THREE.Points(geometry, material)
    scene.add(pointCloud)

    // Subtle rotation and parallax
    let raf = 0
    const mouse = new THREE.Vector2(0, 0)

    function onMouseMove(e) {
      const rect = mount.getBoundingClientRect()
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
      mouse.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1)
    }
    window.addEventListener('mousemove', onMouseMove)

    // Simple animation - just rotation
    function animate() {
      raf = requestAnimationFrame(animate)
      
      pointCloud.rotation.y += 0.003
      pointCloud.rotation.x += 0.001

      camera.position.x += (mouse.x * 8 - camera.position.x) * 0.05
      camera.position.y += (mouse.y * 8 - camera.position.y) * 0.05
      camera.lookAt(0, 0, 0)

      renderer.render(scene, camera)
    }
    animate()

    function onResize() {
      const w = mount.clientWidth || 800
      let h = Math.round((w * 9) / 16)
      if (!h || h < 300) h = 360
      renderer.setSize(w, h)
      camera.aspect = w / h
      camera.updateProjectionMatrix()
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('mousemove', onMouseMove)
      mount.removeChild(renderer.domElement)
      geometry.dispose()
      material.dispose()
      renderer.dispose()
    }
  }, [pointsCount])

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
        <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

        <div className="relative z-10 py-16 px-6">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 text-center">
              <h1 className="text-3xl font-bold tracking-tight sm:text-5xl">{t('memory3d.title')}</h1>
              <p className="mt-2 text-slate-400">{t('memory3d.subtitle')}</p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-2xl p-4">
              <div ref={mountRef} className="mx-auto aspect-video w-full" style={{ minHeight: 360 }} />
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
              <div>{t('memory3d.pointsShown')} <span className="text-slate-200">{Math.max(1, pointsCount || 2000)}</span></div>
              {loading && <div className="flex items-center gap-2"><span className="animate-spin inline-block h-3 w-3 rounded-full border-b-2 border-emerald-400" /> {t('stats.loading')}</div>}
              {err && <div className="text-red-300">{err}</div>}
            </div>

            {/* Chat bar like GPT */}
            <div className="mt-6 mx-auto max-w-4xl">
              <div 
                className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <input
                  type="text"
                  placeholder={t('memory3d.soonInputPlaceholder')}
                  disabled
                  className="w-full px-6 py-4 bg-transparent text-white placeholder-slate-400 resize-none border-0 outline-none disabled:cursor-not-allowed"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-medium">{t('common.soon')}</div>
                
                {/* Tooltip bubble */}
                {showTooltip && (
                  <div className="absolute -top-24 left-1/2 -translate-x-1/2 z-50">
                    <div className="bg-slate-800 text-white text-base px-8 py-6 rounded-xl shadow-lg border border-white/10 max-w-md text-center">
                      <div className="font-medium mb-2 text-lg">{t('memory3d.tooltipTitle')}</div>
                      <div className="text-slate-300 text-sm leading-relaxed">{t('memory3d.tooltipBody')}</div>
                      {/* Arrow pointing down */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-slate-800"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
